// frontend/src/pages/ProfileSecurity.tsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

type MeResponse = {
  user?: {
    id: number;
    customer_type?: "individual" | "business" | string | null;
    two_factor_confirmed_at?: string | null;
  };
};

type LoginActivityResponse = {
  warning?: { message: string; event: LoginEvent } | null;
  events: LoginEvent[];
};

type LoginEvent = {
  id: number;
  event: string;
  ip_address?: string | null;
  user_agent?: string | null;
  is_new_ip?: boolean;
  created_at?: string | null;
};

export default function ProfileSecurity() {
  const navigate = useNavigate();
  const [me, setMe] = React.useState<MeResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [activity, setActivity] = React.useState<LoginActivityResponse | null>(null);

  // Change password form state
  const [form, setForm] = React.useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  const [flash, setFlash] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    api
      .get("/xapi/me")
      .then((data) => {
        if (!mounted) return;
        setMe(data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    api
      .get("/xapi/security/login-activity")
      .then((data) => mounted && setActivity(data as LoginActivityResponse))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const customerType = me?.user?.customer_type ?? null;
  const twoFactorEnabled = !!me?.user?.two_factor_confirmed_at;

  async function submitPasswordUpdate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    setFlash(null);
    try {
      // Blade uses PUT route('password.update') — we expose the same via XAPI PasswordController
      await api.put("/xapi/password/update", {
        current_password: form.current_password,
        password: form.password,
        password_confirmation: form.password_confirmation,
      });
      setFlash("Password updated successfully.");
      setForm({ current_password: "", password: "", password_confirmation: "" });
      // Optional: navigate or just show flash; Blade stays on page, so we stay.
    } catch (err: any) {
      // Expect Laravel validation shape { errors: { field: [..] } }
      const v = err?.errors || {};
      setErrors(v);
      setFlash(err?.message || "Unable to update password.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="skeleton h-6 w-48 mb-4" />
        <div className="skeleton h-10 w-full max-w-2xl" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6">
      {/* Header (Blade: text-xl font-bold) */}
      <h1 className="text-xl font-bold text-gray-800 mb-4">Security Settings</h1>

      {/* Navigation Tabs (Blade parity) */}
      <nav className="flex space-x-4 border-b mb-6">
        <Link
          to="/profile/overview"
          className="text-sm gray-600 hover:text-blue-600 px-3 py-2"
        >
          Overview
        </Link>
        <Link
          to="/profile/edit"
          className="text-sm gray-600 hover:text-blue-600 px-3 py-2"
        >
          Edit Profile
        </Link>

        {customerType === "individual" && (
          <Link
            to="/kyc"
            className="text-sm gray-600 hover:text-blue-600 px-3 py-2"
          >
            KYC Status
          </Link>
        )}
        {customerType === "business" && (
          <Link
            to="/kyb/status"
            className="text-sm gray-600 hover:text-blue-600 px-3 py-2"
          >
            KYB Status
          </Link>
        )}

        {/* Active tab */}
        <span className="border-b-2 border-blue-600 text-sm blue-600 px-3 py-2">
          Security
        </span>

        {customerType === "business" && (
          <Link
            to="/profile/users"
            className="text-sm gray-600 hover:text-blue-600 px-3 py-2"
          >
            Manage Users
          </Link>
        )}
      </nav>

      {/* Change Password */}
      <div className="bg-white rounded p-6 shadow max-w-3xl mb-8">
        <h3 className="text-lg font-bold mb-4">Change Password</h3>

        {flash && (
          <div
            className={`mb-4 ${
              Object.keys(errors).length ? "alert-danger" : "alert-success"
            }`}
          >
            {flash}
          </div>
        )}

        {Object.keys(errors).length > 0 && (
          <div className="mb-4 bg-red-100 text-red-700 px-4 py-3 rounded">
            <ul className="list-disc pl-5">
              {Object.entries(errors).map(([field, msgs]) =>
                msgs.map((m, i) => <li key={`${field}-${i}`}>{m}</li>)
              )}
            </ul>
          </div>
        )}

        <form onSubmit={submitPasswordUpdate}>
          <div className="mb-4">
            <label className="block text-sm gray-700 font-semibold">
              Current Password
            </label>
            <input
              type="password"
              name="current_password"
              className="w-full px-3 py-2 border rounded mt-1"
              required
              value={form.current_password}
              onChange={(e) =>
                setForm((f) => ({ ...f, current_password: e.target.value }))
              }
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm gray-700 font-semibold">
              New Password
            </label>
            <input
              type="password"
              name="password"
              className="w-full px-3 py-2 border rounded mt-1"
              required
              value={form.password}
              onChange={(e) =>
                setForm((f) => ({ ...f, password: e.target.value }))
              }
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm gray-700 font-semibold">
              Confirm New Password
            </label>
            <input
              type="password"
              name="password_confirmation"
              className="w-full px-3 py-2 border rounded mt-1"
              required
              value={form.password_confirmation}
              onChange={(e) =>
                setForm((f) => ({ ...f, password_confirmation: e.target.value }))
              }
            />
          </div>

          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-sm text-white px-4 py-2 rounded disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded p-6 shadow max-w-3xl mb-8">
        <h3 className="text-lg font-bold mb-4">Two-Factor Authentication (2FA)</h3>

        {twoFactorEnabled ? (
          <>
            <p className="text-sm text-green-700 mb-4">
              Two-Factor Authentication is currently <strong>enabled</strong> on your
              account.
            </p>
            {/* Blade had a POST "#" placeholder. In SPA we deep-link to 2FA manager page. */}
            <button
              onClick={() => navigate("/profile/2fa")}
              className="bg-red-600 hover:bg-red-700 text-sm text-white px-4 py-2 rounded"
            >
              Manage / Disable Two-Factor
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-4">
              Two-Factor Authentication is currently <strong>disabled</strong>.
            </p>
            <button
              onClick={() => navigate("/profile/2fa")}
              className="bg-blue-600 hover:bg-blue-700 text-sm text-white px-4 py-2 rounded"
            >
              Enable Two-Factor
            </button>
          </>
        )}
      </div>

      {/* Trusted Devices - Placeholder */}
      <div className="bg-white rounded p-6 shadow max-w-3xl">
        <h3 className="text-lg font-bold mb-4">Login Activity</h3>
        {activity?.warning && (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {activity.warning.message} IP: {activity.warning.event.ip_address || "unknown"}
          </div>
        )}
        <div className="space-y-3">
          {(activity?.events || []).slice(0, 8).map(event => (
            <div key={event.id} className="rounded border px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">{event.ip_address || "Unknown IP"}</span>
                <span className={event.event === "success" ? "text-green-700" : "text-red-700"}>{event.event}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">{event.created_at || ""}</div>
              <div className="mt-1 truncate text-xs text-gray-500">{event.user_agent || ""}</div>
            </div>
          ))}
          {!activity?.events?.length && <p className="text-sm text-gray-700">No login activity recorded yet.</p>}
        </div>
      </div>
    </div>
  );
}
